import React from 'react';
import { Box, Typography, IconButton, Tooltip, Button } from '@mui/material';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import EmailIcon from '@mui/icons-material/Email';
import CopyToClipboard from 'react-copy-to-clipboard';
import { PricingCard } from './PricingCard';
import { useAlert } from '../../contexts/alert/useAlert';
import { useTracking } from '../../analytics/useTracking';
import { Action, Category } from '../../analytics/Tracking';

interface PaymentLinkCardProps {
  paymentLink: string;
  userId: string;
  monthlyPrice?: number;
  discountPercent?: number;
  discountMonths?: number;
  onCopy?: () => void;
}

function buildManagerEmailHref(paymentLink: string, monthlyPrice?: number, discountPercent?: number, discountMonths?: number): string {
  const subject = 'Planning Poker Premium – Subscription Approval';

  const hasDiscount = discountPercent && discountPercent > 0 && discountMonths && monthlyPrice;
  const discountedPrice = hasDiscount
    ? (monthlyPrice * (1 - discountPercent / 100)).toFixed(2)
    : null;

  const priceLine = hasDiscount
    ? `Cost: $${monthlyPrice?.toFixed(2)}/month (${discountPercent}% off for the first ${discountMonths} months — $${discountedPrice}/month for ${discountMonths} months, then $${monthlyPrice?.toFixed(2)}/month thereafter).`
    : monthlyPrice
    ? `Cost: $${monthlyPrice.toFixed(2)}/month.`
    : '';

  const body = `Hi,

I've been using Storypoint Poker (https://storypoint.poker) for our sprint planning sessions — it's browser-based planning poker that works for both remote and in-person teams, with no installs required.

I'd like to continue with a premium subscription:

  - Unlimited participants per session
  - Advanced session insights and history
  - Moderator controls

${priceLine}

You can pay directly with a card via the link below. No account is needed — it goes straight to a secure Stripe checkout:

${paymentLink}

Happy to answer any questions. Thank you!`;

  return `mailto:?subject=${encodeURIComponent(subject)}&body=${encodeURIComponent(body)}`;
}

export function PaymentLinkCard({ paymentLink, userId, monthlyPrice, discountPercent, discountMonths, onCopy }: PaymentLinkCardProps) {
  const alert = useAlert();
  const { track } = useTracking();

  const handleCopy = () => {
    track.event(Category.User, Action.PurchaseLinkCreated, userId);
    alert.showMessage("Payment link copied to clipboard! 💸", "success");
    onCopy?.();
  };

  const mailtoHref = buildManagerEmailHref(paymentLink, monthlyPrice, discountPercent, discountMonths);

  return (
    <>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 2, textAlign: 'center' }}>
        Need manager approval? Send them a direct link — they can pay without creating an account.
      </Typography>
      <PricingCard sx={{
        p: 3,
        display: 'flex',
        flexDirection: 'column',
        gap: 2
      }}>
        <Box sx={{
          display: 'flex',
          gap: 1,
          alignItems: 'flex-start',
          wordBreak: 'break-all'
        }}>
          <Typography
            variant="body2"
            sx={{
              flex: 1,
              fontSize: '0.875rem',
              color: 'text.primary',
              fontFamily: 'monospace'
            }}
          >
            {paymentLink}
          </Typography>
          <CopyToClipboard text={paymentLink} onCopy={handleCopy}>
            <Tooltip title="Copy link">
              <IconButton
                size="small"
                color="secondary"
                sx={{ flexShrink: 0 }}
              >
                <ContentCopyIcon fontSize="small" />
              </IconButton>
            </Tooltip>
          </CopyToClipboard>
        </Box>
        <Button
          component="a"
          href={mailtoHref}
          variant="outlined"
          size="small"
          startIcon={<EmailIcon />}
          fullWidth
        >
          Send to Manager
        </Button>
      </PricingCard>
    </>
  );
}

